import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const BottomPanelContext = createContext(null);

const STORAGE_KEY = 'bottom_panel_height';
const MIN_H = 140;
const MAX_H_RATIO = 0.6;

export function BottomPanelProvider({ children }) {
  const [isOpen, _setOpen] = useState(false);
  const [panelHeight, setPanelHeightRaw] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? Math.max(MIN_H, parseInt(saved, 10)) : 320;
  });
  // content: { tabs: [{ id, label, component }], defaultTab }
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const setOpen = useCallback((open) => {
    _setOpen(open);
    document.body.classList.toggle('bottom-panel-open', open);
  }, []);

  const setPanelHeight = useCallback((h) => {
    const clamped = Math.max(MIN_H, Math.min(Math.floor(window.innerHeight * MAX_H_RATIO), h));
    setPanelHeightRaw(clamped);
    localStorage.setItem(STORAGE_KEY, String(clamped));
  }, []);

  const registerContent = useCallback((newContent) => {
    setContent(newContent);
    setActiveTab(newContent?.defaultTab ?? newContent?.tabs?.[0]?.id ?? null);
  }, []);

  const clearContent = useCallback(() => {
    setContent(null);
    setActiveTab(null);
    setOpen(false);
  }, [setOpen]);

  // 콘텐츠 없으면 열려있어도 닫음
  useEffect(() => {
    if (!content) setOpen(false);
  }, [content, setOpen]);

  return (
    <BottomPanelContext.Provider value={{
      isOpen, setOpen,
      panelHeight, setPanelHeight,
      content, activeTab, setActiveTab,
      registerContent, clearContent,
    }}>
      {children}
    </BottomPanelContext.Provider>
  );
}

export function useBottomPanel() {
  const ctx = useContext(BottomPanelContext);
  if (!ctx) throw new Error('useBottomPanel must be used inside BottomPanelProvider');
  return ctx;
}
